import { useState } from "react";
import { Plus } from "lucide-react";
import { v4 } from "uuid";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { TextInputField } from "@/shared/components/field/input-field";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  ErrValidationFailedContextProvider,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { useOmniContractCreateScopeOfWorkMutation } from "../api/contract-api";
import { Contract } from "../types/contract";
import { ScopeOfWorkBaseDataSelect } from "../../scope-of-work/components/scope-of-work-base-data-select";
import { ScopeOfWorkBaseData } from "../../scope-of-work/types/base-data";

/**
 * ContractScopeOfWorkAddDialogConfig configures the
 * {@link ContractScopeOfWorkAddDialog} per service kind. With selectBaseData,
 * the user selects the base data filling the new scope of work.
 */
export type ContractScopeOfWorkAddDialogConfig = {
  selectBaseData: boolean;
};

/**
 * ContractScopeOfWorkAddDialog adds a new scope of work with a title to the
 * contract. Its kind and other defaults come from the base data. onCreated
 * receives its id.
 */
export function ContractScopeOfWorkAddDialog({
  contract,
  config,
  onCreated,
  disabled,
}: {
  contract: Contract;
  config: ContractScopeOfWorkAddDialogConfig;
  onCreated: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Plus}
        label=""
        variant="outline"
        disabled={disabled}
        aria-label={t("Leistung hinzufügen")}
      />
      <DialogContent>
        {/* the dialog adds a new entity, its errors stay its own */}
        <ErrValidationFailedContextProvider>
          <AddForm
            contract={contract}
            config={config}
            onCreated={(id) => {
              setOpen(false);
              onCreated(id);
            }}
          />
        </ErrValidationFailedContextProvider>
      </DialogContent>
    </Dialog>
  );
}

function AddForm({
  contract,
  config,
  onCreated,
}: {
  contract: Contract;
  config: ContractScopeOfWorkAddDialogConfig;
  onCreated: (id: string) => void;
}) {
  const [create, { isLoading, error, reset }] =
    useOmniContractCreateScopeOfWorkMutation();
  const { write } = useErrValidationFailedContext();
  const [scopeOfWorkId] = useState(v4());
  const [title, setTitle] = useState("");
  const [baseDataId, setBaseDataId] = useState<string>();

  const selectBaseData = (baseData?: ScopeOfWorkBaseData) => {
    setBaseDataId(baseData?.id);
    setTitle(baseData?.title ?? "");
  };

  const onAdd = () => {
    if (isLoading) {
      return;
    }
    create({ id: contract.id, scopeOfWorkId, baseDataId, title })
      .unwrap()
      .then(() => {
        reset();
        onCreated(scopeOfWorkId);
      })
      .catch((e) => {
        write(e);
      });
  };

  return (
    <>
      <CommonDialogHeader
        title={t("Leistung hinzufügen")}
        description={t(
          "Die Leistung fasst Positionen zusammen, z. B. für ein Objekt oder eine Art von Arbeiten.",
        )}
      />
      {config.selectBaseData && (
        <ScopeOfWorkBaseDataSelect
          companyId={contract.companyId}
          serviceKind={contract.serviceKind}
          value={baseDataId}
          onBaseDataChange={selectBaseData}
          fieldName="baseDataId"
        />
      )}
      <TextInputField
        label={t("Titel")}
        text={title}
        onTextChange={setTitle}
        fieldName={`scopeOfWork.${scopeOfWorkId}.title`}
        debounce={false}
      />
      <RTKQueryErrorAlert error={error} />
      <CommonDialogFooter
        isLoading={isLoading}
        onClickAction={onAdd}
        actionLabel={t("Hinzufügen")}
      />
    </>
  );
}
